import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Brand } from "../components/Brand";
import { ScoreGauge } from "../components/ScoreGauge";
import {
  ArrowRight, Bot, UserCheck, Code2, Target, Search, TrendingUp,
  GraduationCap, BadgeCheck, Star, Check, Menu, X, Building2, Sparkles,
  Video, Phone, ChevronDown, User, Loader2,
} from "lucide-react";

function LandingPlanCard({ plan, cur, popular, onSelect }) {
  const priceText = plan.price === 0 ? "Free"
    : plan.price == null ? (plan.type === "payg" ? `${cur}${plan.payg_prices.ai}+` : "Custom")
    : `${cur}${plan.price.toLocaleString()}`;
  return (
    <div className={`rounded-2xl p-7 border relative flex flex-col ${popular ? "border-brand bg-slate-900 text-white shadow-ambient" : "border-slate-200 bg-white"}`}>
      {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-3 py-1 rounded-full">Most Popular</span>}
      <div className={`text-sm font-bold ${popular ? "text-white" : "text-ink"}`}>{plan.name}</div>
      <div className={`text-xs mb-4 ${popular ? "text-blue-200" : "text-ink-faint"}`}>{plan.tagline}</div>
      <div className="font-heading text-3xl font-extrabold num mb-1">{priceText}</div>
      {plan.credits ? <div className={`text-xs mb-5 ${popular ? "text-blue-200" : "text-ink-faint"} num`}>{plan.credits} interview credits</div>
        : plan.period && plan.price ? <div className={`text-xs mb-5 ${popular ? "text-blue-200" : "text-ink-faint"}`}>/ {plan.period}</div>
        : <div className="mb-5" />}
      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.slice(0, 6).map((f) => (
          <li key={f} className={`flex items-start gap-2.5 text-sm ${popular ? "text-blue-50" : "text-ink-soft"}`}><Check className={`h-4 w-4 mt-0.5 shrink-0 ${popular ? "text-white" : "text-emerald-500"}`} /> {f.replace(/\s*[—–-]\s*₹[\d,]+/, "")}</li>
        ))}
      </ul>
      <button data-testid={`landing-plan-${plan.id}`} onClick={onSelect}
        className={`w-full text-sm font-bold px-5 py-3 rounded-full transition-colors ${popular ? "bg-white text-brand hover:bg-blue-50" : "bg-slate-100 text-ink hover:bg-slate-200"}`}>
        {plan.type === "contact" ? "Contact Us" : plan.price === 0 ? "Start Free" : "Get Started"}
      </button>
    </div>
  );
}

function PricingSection() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("individual");
  useEffect(() => { api.get("/billing/plans").then((r) => setD(r.data)).catch(() => {}); }, []);

  const plans = d ? (tab === "individual" ? d.individual_plans : d.college_plans) : [];
  const cur = d?.currency || "₹";

  return (
    <section id="pricing" className="py-20 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="text-xs font-bold uppercase tracking-wider text-brand mb-3">Pricing</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-ink">Simple, transparent plans</h2>
          <p className="mt-3 text-ink-soft">Choose an individual plan or empower your whole institution.</p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="flex gap-1 bg-slate-100 rounded-full p-1">
            <button data-testid="landing-tab-individual" onClick={() => setTab("individual")} className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-colors ${tab === "individual" ? "bg-white text-ink shadow-sm" : "text-ink-soft"}`}><User className="h-4 w-4" /> Individual</button>
            <button data-testid="landing-tab-college" onClick={() => setTab("college")} className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-colors ${tab === "college" ? "bg-white text-ink shadow-sm" : "text-ink-soft"}`}><Building2 className="h-4 w-4" /> For Colleges</button>
          </div>
        </div>

        {!d ? (
          <div className="min-h-[240px] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start" data-testid={`landing-pricing-${tab}`}>
            {plans.map((p) => (
              <LandingPlanCard key={p.id} plan={p} cur={cur} popular={p.popular} onSelect={() => navigate("/pricing")} />
            ))}
          </div>
        )}
        <p className="text-center text-xs text-ink-faint mt-8">Payments are simulated in this demo. All prices are configurable by the Platform Admin.</p>
      </div>
    </section>
  );
}

const NAV = [
  { label: "How It Works", href: "#how" },
  { label: "AI Interviews", children: [
      { label: "🎥 AI Video Interview", href: "#ai-video" },
      { label: "📞 AI Voice Interview", href: "#ai-voice" },
    ] },
  { label: "Expert Interview", href: "#expert" },
  { label: "Coding Assessment", href: "#coding" },
  { label: "For Colleges", href: "#colleges" },
  { label: "Pricing", href: "#pricing" },
];

const STEPS = [
  { icon: Target, title: "Choose Your Target Role", desc: "Select the job you want to prepare for." },
  { icon: Bot, title: "Take a Realistic Interview", desc: "Practice with AI or industry experts." },
  { icon: BadgeCheck, title: "Get Assessed", desc: "Measure technical, coding, communication, behavioral and problem-solving skills." },
  { icon: Search, title: "Discover Your Skill Gaps", desc: "Understand exactly where you need improvement." },
  { icon: TrendingUp, title: "Improve", desc: "Get personalized recommendations and an improvement roadmap." },
  { icon: GraduationCap, title: "Upskill", desc: "Access recommended courses and resources." },
  { icon: ArrowRight, title: "Re-interview", desc: "Take another interview and measure your improvement." },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`sticky top-0 z-50 ${scrolled ? "glass border-b border-slate-200" : "bg-transparent"} transition-colors`}>
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Brand />
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            n.children ? (
              <div key={n.label} className="relative group">
                <button className="text-sm font-semibold text-ink-soft hover:text-ink transition-colors inline-flex items-center gap-1">
                  {n.label} <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="w-56 rounded-2xl border border-slate-200 bg-white shadow-lift p-2">
                    {n.children.map((c) => (
                      <a key={c.href} href={c.href} data-testid={`nav-sub-${c.href.replace('#', '')}`} className="block px-3 py-2.5 rounded-xl text-sm font-semibold text-ink-soft hover:bg-slate-100 hover:text-ink transition-colors">{c.label}</a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a key={n.href} href={n.href} className="text-sm font-semibold text-ink-soft hover:text-ink transition-colors">{n.label}</a>
            )
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" data-testid="header-login" className="text-sm font-semibold text-ink hover:text-brand px-3 py-2">Login</Link>
          <Link to="/login" data-testid="header-cta" className="text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full shadow-ambient hover:-translate-y-0.5 transition-transform">
            Start Your Mock Interview
          </Link>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="landing-menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden glass border-b border-slate-200 px-5 py-4 space-y-3">
          {NAV.map((n) => (
            n.children ? (
              <div key={n.label} className="space-y-2">
                <div className="text-sm font-bold text-ink">{n.label}</div>
                {n.children.map((c) => <a key={c.href} href={c.href} onClick={() => setOpen(false)} className="block text-sm font-semibold text-ink-soft pl-3">{c.label}</a>)}
              </div>
            ) : (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block text-sm font-semibold text-ink-soft">{n.label}</a>
            )
          ))}
          <Link to="/login" className="block text-sm font-bold text-white bg-brand text-center px-5 py-2.5 rounded-full">Start Your Mock Interview</Link>
        </div>
      )}
    </header>
  );
}

function HeroVisual() {
  const bars = [
    { k: "Technical", v: 82 }, { k: "Coding", v: 88 }, { k: "Problem Solving", v: 76 },
    { k: "Communication", v: 71 }, { k: "Behavioural", v: 80 },
  ];
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-brand/10 blur-3xl rounded-full" />
      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-lift p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-faint">Einstein Score</div>
            <div className="text-sm font-bold text-ink">Software Engineer</div>
          </div>
          <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">Almost Ready</span>
        </div>
        <div className="flex items-center gap-6">
          <ScoreGauge value={79} size={140} label={false} />
          <div className="flex-1 space-y-2.5">
            {bars.map((b) => (
              <div key={b.k}>
                <div className="flex justify-between text-[11px] font-semibold text-ink-soft mb-1">
                  <span>{b.k}</span><span className="num">{b.v}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-brand" style={{ width: `${b.v}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute -bottom-5 -left-5 bg-white rounded-xl border border-slate-200 shadow-card px-4 py-3 hidden sm:flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-emerald-100 grid place-items-center"><TrendingUp className="h-4 w-4 text-emerald-600" /></span>
        <div><div className="text-[10px] text-ink-faint font-semibold">Improvement</div><div className="text-sm font-bold text-emerald-600 num">+18 pts</div></div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-5 pt-16 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-brand bg-brand-light px-3 py-1.5 rounded-full mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Practice. Perform. Crack Your Interview.
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-ink">
              Practice Smarter.<br />Interview Better.<br /><span className="text-brand">Crack Your Dream Job.</span>
            </h1>
            <p className="mt-6 text-ink-soft text-base sm:text-lg max-w-xl leading-relaxed">
              Experience realistic AI and expert-led mock interviews, coding assessments and personalized career
              recommendations designed to make you interview-ready.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-start" onClick={() => navigate("/login")}
                className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-6 py-3.5 rounded-full shadow-ambient hover:-translate-y-0.5 transition-transform">
                Start Interview <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#colleges" data-testid="hero-colleges"
                className="inline-flex items-center gap-2 text-sm font-bold text-ink bg-white border border-slate-300 hover:border-slate-400 px-6 py-3.5 rounded-full transition-colors">
                <Building2 className="h-4 w-4" /> For Colleges
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-ink-faint font-semibold">
              <span>12+ Job Roles</span><span>·</span><span>AI & Expert Panels</span><span>·</span><span>Verified Scorecards</span>
            </div>
          </div>
          <HeroVisual />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-bold uppercase tracking-wider text-brand mb-3">How Mock2Crack Works</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-ink">From practice to proven readiness</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="rounded-2xl border border-slate-200 p-6 hover:shadow-card hover:-translate-y-1 transition-all bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <span className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center"><Icon className="h-5 w-5 text-brand" /></span>
                    <span className="num text-xs font-bold text-ink-faint">0{i + 1}</span>
                  </div>
                  <div className="font-heading font-bold text-ink">{s.title}</div>
                  <p className="text-sm text-ink-soft mt-1.5 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interview types */}
      <section id="ai" className="py-20">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold uppercase tracking-wider text-brand mb-3">AI Interviews</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-ink">Two ways to practice with AI</h2>
            <p className="mt-3 text-ink-soft">Meet your AI interviewer face-to-face, or get a call from your AI interviewer — whichever fits you.</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* AI Video */}
            <div id="ai-video" className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-card transition-all">
              <div className="flex items-center justify-between mb-5">
                <span className="h-12 w-12 rounded-xl bg-brand-light grid place-items-center"><Video className="h-6 w-6 text-brand" /></span>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-brand-light text-brand">Most Immersive</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-ink">🎥 AI Video Interview</h3>
              <p className="text-ink-soft mt-2">Face-to-face AI interview from your browser.</p>
              <ul className="mt-5 space-y-2.5">
                {["Live AI interviewer", "Candidate webcam & mic", "Real-time conversation", "Coding assessment", "Communication & confidence analysis", "Automated scoring"].map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-ink-soft"><Check className="h-4 w-4 text-emerald-500" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => navigate("/login")} className="mt-6 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full transition-colors">Start AI Video Interview</button>
            </div>
            {/* AI Voice */}
            <div id="ai-voice" className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-card transition-all">
              <div className="flex items-center justify-between mb-5">
                <span className="h-12 w-12 rounded-xl bg-emerald-100 grid place-items-center"><Phone className="h-6 w-6 text-emerald-600" /></span>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">Interview Anywhere</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-ink">📞 AI Voice Interview</h3>
              <p className="text-ink-soft mt-2">Complete your interview through a phone call.</p>
              <ul className="mt-5 space-y-2.5">
                {["AI interviewer calls your phone", "No webcam or computer required", "Real-time voice conversation", "Technical & behavioral questions", "Voice communication analysis", "Automated scoring"].map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-ink-soft"><Check className="h-4 w-4 text-emerald-500" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => navigate("/login")} className="mt-6 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-5 py-2.5 rounded-full transition-colors">Start AI Voice Interview</button>
            </div>
            {/* Human Expert */}
            <div id="expert" className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-card transition-all">
              <div className="flex items-center justify-between mb-5">
                <span className="h-12 w-12 rounded-xl bg-slate-900 grid place-items-center"><UserCheck className="h-6 w-6 text-white" /></span>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-ink-soft">Industry Pro</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-ink">👤 Human Expert Interview</h3>
              <p className="text-ink-soft mt-2">Experience a realistic interview with an industry professional.</p>
              <ul className="mt-5 space-y-2.5">
                {["Industry expert", "Video interview simulation", "Technical assessment", "Coding assessment", "Expert feedback", "Detailed scorecard"].map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-ink-soft"><Check className="h-4 w-4 text-emerald-500" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => navigate("/login")} className="mt-6 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-5 py-2.5 rounded-full transition-colors">Choose Expert Interview</button>
            </div>
          </div>
        </div>
      </section>

      {/* Coding */}
      <section id="coding" className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-brand mb-3">Coding Assessment</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Coding, embedded inside every interview</h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Solve role-relevant problems in Java, Python, JavaScript, C++ or SQL. See test cases, execution time and a
              coding score that feeds directly into your interview result.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Java", "Python", "JavaScript", "C++", "SQL"].map((l) => (
                <span key={l} className="text-xs font-bold num px-3 py-1.5 rounded-full bg-white/10 border border-white/10">{l}</span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-slate-950 border border-white/10 overflow-hidden shadow-lift">
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
              <span className="h-3 w-3 rounded-full bg-red-400" /><span className="h-3 w-3 rounded-full bg-amber-400" /><span className="h-3 w-3 rounded-full bg-emerald-400" />
              <span className="ml-3 text-xs text-slate-400 num">two_sum.py</span>
            </div>
            <pre className="num text-[13px] leading-6 p-5 text-slate-200 overflow-x-auto"><code>{`def two_sum(nums, target):
    seen = {}
    for i, n in enumerate(nums):
        if target - n in seen:
            return [seen[target-n], i]
        seen[n] = i
`}</code></pre>
            <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between text-xs">
              <span className="text-emerald-400 font-bold">✓ 6 / 6 test cases passed</span>
              <span className="text-slate-400 num">128 ms · Score 88</span>
            </div>
          </div>
        </div>
      </section>

      {/* For colleges */}
      <section id="colleges" className="py-20">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
          <img src="https://images.pexels.com/photos/31656148/pexels-photo-31656148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Modern university campus" className="rounded-2xl border border-slate-200 shadow-card w-full h-[340px] object-cover" />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-brand mb-3">For Colleges</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-ink">Make every student interview-ready</h2>
            <p className="mt-4 text-ink-soft leading-relaxed">
              Sponsor interviews, track readiness across cohorts, spot skill gaps and prove placement outcomes — all from
              a dedicated college dashboard with credits and analytics.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {["Student management", "Sponsored interviews", "Readiness analytics", "Skill-gap insights", "Interview credits", "Verified scorecards"].map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-ink-soft font-medium"><BadgeCheck className="h-4 w-4 text-brand" /> {f}</li>
              ))}
            </ul>
            <button onClick={() => navigate("/login")} className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-6 py-3 rounded-full transition-colors">
              Explore College Dashboard <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5">
          <div className="rounded-3xl bg-slate-900 text-white p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-grid opacity-20" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Ready to crack your next interview?</h2>
              <p className="mt-3 text-slate-300 max-w-xl mx-auto">Take your first mock interview and get your Einstein Score in minutes.</p>
              <button onClick={() => navigate("/login")} data-testid="cta-start"
                className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-7 py-3.5 rounded-full shadow-ambient hover:-translate-y-0.5 transition-transform">
                Start Your Mock Interview <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Brand />
          <p className="text-sm text-ink-faint">© 2026 Mock2Crack. Practice. Perform. Crack Your Interview.</p>
        </div>
      </footer>
    </div>
  );
}
